import { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import { THEME } from "../utils/theme";

/**
 * ForceGraph — renders the D3 force-directed network.
 */
export default function ForceGraph({
  nodes,
  links,
  width,
  height,
  onNodeHover,
  onNodeClick,
  onNodeRoute,
}) {
  const svgRef = useRef(null);
  const simRef = useRef(null);

  const buildGraph = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    if (!nodes.length) return;

    // ── Defs: glow filters & arrow markers ──
    const defs = svg.append("defs");

    Object.entries(THEME.nodeColors).forEach(([type]) => {
      const filter = defs
        .append("filter")
        .attr("id", `glow-${type}`)
        .attr("x", "-50%").attr("y", "-50%")
        .attr("width", "200%").attr("height", "200%");
      filter.append("feGaussianBlur").attr("in", "SourceGraphic").attr("stdDeviation", "4").attr("result", "blur");
      filter.append("feColorMatrix").attr("in", "blur").attr("type", "matrix")
        .attr("values", "1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.6 0");
      filter.append("feMerge").selectAll("feMergeNode")
        .data(["blur", "SourceGraphic"]).enter()
        .append("feMergeNode").attr("in", (d) => d === "blur" ? "blur" : "SourceGraphic");
    });

    Object.entries(THEME.linkColors).forEach(([type, color]) => {
      defs.append("marker")
        .attr("id", `arrow-${type}`).attr("viewBox", "0 -5 10 10")
        .attr("refX", 20).attr("refY", 0)
        .attr("markerWidth", 6).attr("markerHeight", 6).attr("orient", "auto")
        .append("path").attr("d", "M0,-4L10,0L0,4").attr("fill", color);
    });

    const g = svg.append("g");

    // ── Zoom ──
    svg.call(
      d3.zoom().scaleExtent([0.15, 4]).on("zoom", (event) => g.attr("transform", event.transform))
    );

    // ── Simulation ──
    const sim = d3.forceSimulation(nodes)
      .force("link", d3.forceLink(links).id((d) => d.id)
        .distance((d) => (d.type === "sector" ? 180 : d.type === "ownership" || d.type === "affiliation" ? 120 : 100))
        .strength(0.4))
      .force("charge", d3.forceManyBody().strength((d) => (d.type === "sector" ? -600 : d.type === "company" ? -350 : -200)))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius((d) => d.size + 8))
      .force("x", d3.forceX(width / 2).strength(0.03))
      .force("y", d3.forceY(height / 2).strength(0.03));

    simRef.current = sim;

    // ── Links ──
    const link = g.append("g").selectAll("line").data(links).enter().append("line")
      .attr("stroke", (d) => THEME.linkColors[d.type] || "#333")
      .attr("stroke-width", (d) => (d.type === "referral" || d.type === "shared" ? 1.5 : d.type === "ownership" ? 2 : 1))
      .attr("stroke-dasharray", (d) => (d.type === "referral" ? "6,4" : d.type === "shared" ? "3,3" : "none"))
      .attr("opacity", 0.7)
      .attr("marker-end", (d) => (d.type === "referral" ? `url(#arrow-${d.type})` : ""));

    // ── Nodes ──
    const node = g.append("g").selectAll("g").data(nodes).enter().append("g").attr("cursor", "pointer");

    node.each(function (d) {
      const el = d3.select(this);
      const colors = THEME.nodeColors[d.type];
      const r = d.type === "sector" ? d.size * 0.9
        : d.type === "company" ? d.size * 0.75
        : d.type === "opportunity" ? d.size * 0.7
        : 7;

      if (d.type === "sector") {
        const hex = (r) => Array.from({ length: 6 }, (_, i) => {
          const a = (Math.PI / 3) * i - Math.PI / 6;
          return `${r * Math.cos(a)},${r * Math.sin(a)}`;
        }).join(" ");
        el.append("polygon").attr("points", hex(r))
          .attr("fill", colors.fill + "22").attr("stroke", colors.stroke).attr("stroke-width", 2);
      } else if (d.type === "company") {
        el.append("rect").attr("x", -r).attr("y", -r * 0.7).attr("width", r * 2).attr("height", r * 1.4).attr("rx", 4)
          .attr("fill", colors.fill + "22").attr("stroke", colors.stroke).attr("stroke-width", 1.5);
      } else if (d.type === "opportunity") {
        el.append("polygon").attr("points", `0,${-r} ${r},0 0,${r} ${-r},0`)
          .attr("fill", colors.fill + "22").attr("stroke", colors.stroke).attr("stroke-width", 1.5);
      } else {
        el.append("circle").attr("r", r)
          .attr("fill", colors.fill + "33").attr("stroke", colors.stroke).attr("stroke-width", 1.5);
      }

      el.append("text")
        .text(d.label)
        .attr("dy", d.type === "contact" ? r + 14 : r + (d.type === "sector" ? 16 : 14))
        .attr("text-anchor", "middle")
        .attr("fill", THEME.textSecondary)
        .attr("font-size", d.type === "sector" ? "11px" : "9.5px")
        .attr("font-weight", d.type === "sector" ? 600 : 400)
        .style("font-family", "inherit");
    });

    // ── Interactions ──
    node
      .on("mouseenter", function (event, d) {
        onNodeHover?.(d, { x: event.pageX, y: event.pageY });

        const connected = new Set([d.id]);
        links.forEach((l) => {
          const sid = l.source.id || l.source;
          const tid = l.target.id || l.target;
          if (sid === d.id) connected.add(tid);
          if (tid === d.id) connected.add(sid);
        });

        node.transition().duration(200).attr("opacity", (n) => (connected.has(n.id) ? 1 : 0.12));
        link.transition().duration(200)
          .attr("opacity", (l) => ((l.source.id || l.source) === d.id || (l.target.id || l.target) === d.id ? 1 : 0.04))
          .attr("stroke-width", (l) => ((l.source.id || l.source) === d.id || (l.target.id || l.target) === d.id ? 3 : 1));

        d3.select(this).select("circle, rect, polygon")
          .attr("filter", `url(#glow-${d.type})`)
          .attr("fill", THEME.nodeColors[d.type].fill + "44");
      })
      .on("mousemove", (event) => onNodeHover?.(null, { x: event.pageX, y: event.pageY }, true))
      .on("mouseleave", function (event, d) {
        onNodeHover?.(null);
        node.transition().duration(300).attr("opacity", 1);
        link.transition().duration(300).attr("opacity", 0.7)
          .attr("stroke-width", (d) => (d.type === "ownership" ? 2 : d.type === "referral" || d.type === "shared" ? 1.5 : 1));
        d3.select(this).select("circle, rect, polygon")
          .attr("filter", null)
          .attr("fill", THEME.nodeColors[d.type].fill + "22");
      })
      .on("click", (event, d) => onNodeClick?.(d))
      .on("dblclick", (event, d) => onNodeRoute?.(d));

    // ── Drag ──
    node.call(
      d3.drag()
        .on("start", (event, d) => { if (!event.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
        .on("drag", (event, d) => { d.fx = event.x; d.fy = event.y; })
        .on("end", (event, d) => { if (!event.active) sim.alphaTarget(0); d.fx = null; d.fy = null; })
    );

    // ── Tick ──
    sim.on("tick", () => {
      link.attr("x1", (d) => d.source.x).attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x).attr("y2", (d) => d.target.y);
      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    return () => sim.stop();
  }, [nodes, links, width, height, onNodeHover, onNodeClick, onNodeRoute]);

  useEffect(() => {
    const cleanup = buildGraph();
    return cleanup;
  }, [buildGraph]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      style={{ background: "transparent", display: "block" }}
    />
  );
}
